import React, {useState, useEffect} from 'react'
import {
    Box,
    Heading,
    Link,
    Image,
    Text,
    Wrap,
    WrapItem
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useCustomFetch from '../../../../hooks/useFetchContent'
import PropTypes from 'prop-types'
import {
    sectionContainerStyles,
    infoContainerstyles,
    linkTextStyles,
    contentContainerStyles
} from './index.style'
import LoadingSpinner from '../../../../components/loading-spinner'

const CategorySection = ({title = '', items}) => (
    <Box sx={sectionContainerStyles}>
        <Heading as="h2" size="lg" mb={4}>
            {title}
        </Heading>

        <Wrap spacing={{base: 2, md: 6}} mb={{base: 4, md: 6}} justify="center">
            {items.map((item, idx) => (
                <WrapItem key={idx} flexBasis={{base: '48%', md: '23%'}} justifyContent="center">
                    <Box textAlign="center" w="100%">
                        <Link sx={linkTextStyles} href={item.link}>
                            <Image
                                htmlWidth="472"
                                htmlHeight="756"
                                src={item.img}
                                alt={item.title}
                                objectFit="cover"
                                mx="auto"
                                maxW="100%"
                                title={item.title}
                            />
                            <Text>{item.title}</Text>
                        </Link>
                    </Box>
                </WrapItem>
            ))}
        </Wrap>
    </Box>
)

CategorySection.propTypes = {
    items: PropTypes.array,
    title: PropTypes.string
}

const CategoryContentLanding = ({category}) => {
    const {token} = useAccessToken()
    const {response, loading} = useCustomFetch(token, [
        `FF01:category:${category?.id}`,
        `FF02:category:${category?.id}`,
        `FF03:category:${category?.id}`,
        `FF04:category:${category?.id}`,
        `FF05:category:${category?.id}`,
        `FF06:category:${category?.id}`,
        `FF07:category:${category?.id}`,
        `FF08:category:${category?.id}`,
        `FF09:category:${category?.id}`,
        `FF10:category:${category?.id}`
    ])

    useEffect(() => {
        const initCarousel = (wrapper, containerSelector, scrollAmount = 300) => {
            if (!wrapper) return null

            const container = wrapper.querySelector(containerSelector)
            const leftArrow = wrapper.querySelector('.carousel-arrow-left')
            const rightArrow = wrapper.querySelector('.carousel-arrow-right')

            if (!container || !leftArrow || !rightArrow) return null

            const updateArrowVisibility = () => {
                const scrollLeft = container.scrollLeft
                const maxScroll = container.scrollWidth - container.clientWidth
                const hasOverflow = container.scrollWidth > container.clientWidth
                if (scrollLeft > 0 && hasOverflow) {
                    leftArrow.style.display = 'flex'
                } else {
                    leftArrow.style.display = 'none'
                }
                if (scrollLeft < maxScroll - 1 && hasOverflow) {
                    rightArrow.style.display = 'flex'
                } else {
                    rightArrow.style.display = 'none'
                }
            }

            const scrollContainer = (direction) => {
                const targetScroll =
                    container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount)

                container.scrollTo({
                    left: targetScroll,
                    behavior: 'smooth'
                })
            }

            const onLeftClick = () => scrollContainer('left')
            const onRightClick = () => scrollContainer('right')

            leftArrow.addEventListener('click', onLeftClick)
            rightArrow.addEventListener('click', onRightClick)
            container.addEventListener('scroll', updateArrowVisibility)
            window.addEventListener('resize', updateArrowVisibility)

            updateArrowVisibility()

            return () => {
                leftArrow.removeEventListener('click', onLeftClick)
                rightArrow.removeEventListener('click', onRightClick)
                container.removeEventListener('scroll', updateArrowVisibility)
                window.removeEventListener('resize', updateArrowVisibility)
            }
        }

        const initAllCarousels = (wrapperSelector, containerSelector, scrollAmount) => {
            const cleanups = []
            document.querySelectorAll(wrapperSelector).forEach((wrapper) => {
                const cleanup = initCarousel(wrapper, containerSelector, scrollAmount)
                if (cleanup) cleanups.push(cleanup)
            })
            return () => cleanups.forEach((cleanup) => cleanup())
        }

        if (!response) return undefined

        const cleanupFf2 = initAllCarousels(
            '.icon-carousel-wrapper',
            '.icon-category-navigation',
            100
        )
        const cleanupFf5 = initAllCarousels(
            '.shop-by-brand-carousel-wrapper',
            '.shop-by-brand-carousel',
            200
        )

        return () => {
            cleanupFf2()
            cleanupFf5()
        }
    }, [response])

    if (loading) {
        return <LoadingSpinner />
    }

    return (
        <>
            <Box className="categories">
                {response && (
                    <Box sx={contentContainerStyles} dangerouslySetInnerHTML={{__html: response}} />
                )}
            </Box>
        </>
    )
}

CategoryContentLanding.propTypes = {
    category: PropTypes.object
}

export default CategoryContentLanding
